/**
 * <Toast> — Radix Toast wrapper + `useToast()` imperative hook.
 *
 * Wraps `@radix-ui/react-toast`. Architecture:
 *
 *   - `<Toaster>` provider mounts once near the app root; it wraps Radix
 *     `Toast.Provider` + `Toast.Viewport` and installs the toast event bus
 *     via React context.
 *   - `useToast()` returns `{ toast(opts) }` — consumer components call
 *     `toast({ variant: "success", description: "Saved" })` from any tree
 *     depth.
 *   - The hook queues toasts into a local state that re-renders inside the
 *     `<Toaster>` consumer, rendering one `<ToastItem>` per entry.
 *
 * Auto-dismiss is OURS, not Radix's (`duration={Infinity}` on every root).
 * Radix keeps one pause flag for the whole provider, set when the pointer is
 * over the viewport, when focus lands inside it, or when the window blurs —
 * and its pause/resume listeners are mounted only while a toast exists. Close
 * the last toast while paused and the flag stays set with nothing left to
 * clear it, so every later toast is created with its timer never started and
 * hangs on screen for the rest of the page's life. Here the paused state is
 * derived from three independent facts (pointer over, focus inside, tab
 * hidden) and reset when the stack empties, so no single missed event can
 * leave a toast stuck.
 *
 * Motion honors `prefers-reduced-motion` — the default swipe/slide animation
 * drops to opacity-only via Tailwind's `motion-reduce:` variant (UI-SPEC §RTL
 * & Accessibility).
 *
 * Variants per UI-SPEC §Color semantic palette: neutral / info / success /
 * warning / danger — each uses its soft-tinted background + matching Icon.
 */
import * as React from "react";
import { useTranslation } from "react-i18next";
import * as RadixToast from "@radix-ui/react-toast";
import { AlertOctagon, AlertTriangle, CheckCircle, Info, X, type LucideProps } from "lucide-react";
import { cn, cva, type VariantProps } from "../../utils";
import { Icon } from "../Icon";
import { nonEmpty, type NonEmptyString } from "../../types/NonEmptyString";

type LucideIconComponent = React.ForwardRefExoticComponent<
  Omit<LucideProps, "ref"> & React.RefAttributes<SVGSVGElement>
>;

// ---------------------------------------------------------------------------
// CVA variants — ToastItem visuals
// ---------------------------------------------------------------------------

export const toastVariants = cva(
  cn(
    // Shape — `overflow-hidden` clips the progress bar to the rounded corners.
    "linkhr-glass pointer-events-auto relative flex items-start gap-sm overflow-hidden",
    "w-full max-w-sm rounded-md border shadow-md",
    "p-md text-body",
    // Motion — enter: slide up from block-end + fade, 220ms. Exit: fade.
    "data-[state=open]:animate-in data-[state=open]:slide-in-from-bottom data-[state=open]:fade-in-0 data-[state=open]:motion-toast",
    "data-[state=closed]:animate-out data-[state=closed]:fade-out-80 data-[state=closed]:motion-toast",
    // Swipe-to-dismiss — Radix drives the physical X translate.
    "data-[swipe=move]:translate-x-[var(--radix-toast-swipe-move-x)]",
    "data-[swipe=cancel]:translate-x-0",
    "data-[swipe=end]:animate-out data-[swipe=end]:slide-out-to-start-full",
    // prefers-reduced-motion — opacity-only; no slide or zoom.
    "motion-reduce:data-[state=open]:animate-none motion-reduce:data-[state=open]:fade-in-0",
    "motion-reduce:data-[state=closed]:animate-none",
    "motion-reduce:data-[swipe=move]:translate-x-0",
    "motion-reduce:data-[swipe=end]:animate-none",
    "transition-transform duration-fast ease-out",
  ),
  {
    variants: {
      variant: {
        neutral: "bg-surface-elevated border-subtle text-default",
        info: "bg-info-soft border-info/40 text-default",
        success: "bg-success-soft border-success/40 text-default",
        warning: "bg-warning-soft border-warning/40 text-default",
        danger: "bg-danger-soft border-danger/40 text-default",
      },
    },
    defaultVariants: { variant: "neutral" },
  },
);

export type ToastVariant = NonNullable<VariantProps<typeof toastVariants>["variant"]>;

const VARIANT_ICON: Record<ToastVariant, LucideIconComponent> = {
  neutral: Info,
  info: Info,
  success: CheckCircle,
  warning: AlertTriangle,
  danger: AlertOctagon,
};

const VARIANT_ICON_NAME: Record<ToastVariant, string> = {
  neutral: "info",
  info: "info",
  success: "check-circle",
  warning: "alert-triangle",
  danger: "alert-octagon",
};

// ---------------------------------------------------------------------------
// Decomposed exports
// ---------------------------------------------------------------------------

export const ToastProvider = RadixToast.Provider;
export const ToastRoot = RadixToast.Root;
export const ToastTitle = RadixToast.Title;
export const ToastDescription = RadixToast.Description;
export const ToastAction = RadixToast.Action;
export const ToastClose = RadixToast.Close;
export const ToastViewport = React.forwardRef<
  React.ElementRef<typeof RadixToast.Viewport>,
  React.ComponentPropsWithoutRef<typeof RadixToast.Viewport>
>(function ToastViewport({ className, ...rest }, ref) {
  return (
    <RadixToast.Viewport
      ref={ref}
      className={cn(
        // Viewport — on desktop, bottom-START, just past the sidebar rail.
        // Primary actions live at the bottom-END: every drawer is pinned to
        // the end edge with its Save in the footer, and dialog and page
        // footers align their buttons to the end too. A toast in that corner
        // sat on top of Save, took its clicks and, hovered, never left.
        // Logical insets flip it to the right in RTL, where the drawers open
        // on the left. Phones and tablets show toasts under the top bar so
        // they never cover a composer or the bottom bar.
        // z-[60]: toasts are the TOPMOST feedback layer — they must outrank
        // full-screen overlays like the setup wizard (fixed inset-0 z-50,
        // portaled to <body> AFTER this viewport, so an equal z lost on DOM
        // order and toasts rendered underneath it).
        "fixed bottom-[calc(var(--size-bottomnav-h,0px)+var(--spacing-lg))] md:start-[calc(var(--size-rail-w,0px)+var(--spacing-lg))] max-md:end-lg z-[60] flex max-w-sm flex-col gap-sm max-sm:start-lg max-sm:max-w-none max-md:top-[calc(var(--size-topbar-h,72px)+var(--spacing-sm))] max-md:bottom-auto",
        // Stack is ordered; outlines none so keyboard focus styles come from
        // the individual close button.
        "outline-none",
        className,
      )}
      {...rest}
    />
  );
});

// ---------------------------------------------------------------------------
// Imperative API — useToast()
// ---------------------------------------------------------------------------

export interface ToastOptions {
  /** Variant — defaults to `neutral`. */
  variant?: ToastVariant;
  /** Optional bold title line (role="status"). */
  title?: React.ReactNode;
  /** Required body — every toast needs something to say. */
  description: React.ReactNode;
  /** Auto-dismiss duration (ms). Defaults to 5000. Pass `Infinity` to persist. */
  duration?: number;
  /** Optional action button (e.g. "Undo"). */
  action?: {
    label: React.ReactNode;
    onClick: () => void;
    /** Accessible alt-text for screen readers — required by Radix for non-button actions. */
    altText: string;
  };
  /**
   * Accessible label for the close-X button. Defaults to the localized
   * common "close" label; pass one to say something more specific.
   */
  closeLabel?: NonEmptyString;
}

interface ToastEntry extends ToastOptions {
  id: string;
}

interface ToastContextValue {
  toast: (opts: ToastOptions) => string;
  dismiss: (id: string) => void;
}

const ToastContext = React.createContext<ToastContextValue | null>(null);

/**
 * `useToast()` — imperative entry-point. Must be used inside a `<Toaster>` tree.
 * Returns `{ toast(opts) }` which enqueues a new toast and returns its id.
 */
export function useToast(): ToastContextValue {
  const ctx = React.useContext(ToastContext);
  if (ctx === null) {
    throw new Error("useToast() must be used inside <Toaster>. Mount <Toaster /> at the app root.");
  }
  return ctx;
}

// Internal id counter — stable across renders.
let __toastIdCounter = 0;
function nextToastId(): string {
  __toastIdCounter += 1;
  return `toast-${__toastIdCounter}`;
}

// ---------------------------------------------------------------------------
// <Toaster /> — provider + viewport
// ---------------------------------------------------------------------------

export interface ToasterProps {
  /** Default auto-dismiss duration (ms). Per-toast `duration` overrides. */
  defaultDuration?: number;
  /** Swipe-to-dismiss threshold (px). Defaults to Radix's 50. */
  swipeThreshold?: number;
  /**
   * Optional children rendered inside the ToastContext.Provider so any
   * `useToast()` consumer in the subtree resolves context. When omitted,
   * `<Toaster />` renders as a bare provider + viewport (classic app-root
   * mount pattern — consumers live elsewhere in the tree via React portals).
   * Phase 2 DS-R-11 / Storybook decorators rely on the children form.
   */
  children?: React.ReactNode;
}

export function Toaster({ defaultDuration = 5000, swipeThreshold, children }: ToasterProps = {}) {
  const [toasts, setToasts] = React.useState<ReadonlyArray<ToastEntry>>([]);
  // Why a toast waits: the reader's pointer is on it, their focus is in it, or
  // the tab is in the background. Each is tracked on its own so clearing any
  // one of them is enough to get the countdown moving again.
  const [pointerOver, setPointerOver] = React.useState(false);
  const [focusInside, setFocusInside] = React.useState(false);
  const [tabHidden, setTabHidden] = React.useState(false);
  const hasToasts = toasts.length > 0;

  const dismiss = React.useCallback((id: string) => {
    setToasts((prev) => {
      const next = prev.filter((t) => t.id !== id);
      // The node under the pointer has gone: no pointerleave will follow it,
      // so an empty stack forgets it was ever hovered or focused.
      if (next.length === 0) {
        setPointerOver(false);
        setFocusInside(false);
      }
      return next;
    });
  }, []);

  const toast = React.useCallback((opts: ToastOptions) => {
    const id = nextToastId();
    setToasts((prev) => [...prev, { ...opts, id }]);
    return id;
  }, []);

  const ctxValue = React.useMemo<ToastContextValue>(() => ({ toast, dismiss }), [toast, dismiss]);

  React.useEffect(() => {
    if (!hasToasts) return;
    const read = (): void => setTabHidden(document.visibilityState === "hidden");
    read();
    document.addEventListener("visibilitychange", read);
    return () => document.removeEventListener("visibilitychange", read);
  }, [hasToasts]);

  const paused = pointerOver || focusInside || tabHidden;

  const providerProps = {
    ...(swipeThreshold !== undefined ? { swipeThreshold } : {}),
  };
  // The stack sits at the inline start, so a toast is swiped away toward it.
  // Read at render: a new toast re-renders this provider, so a language switch
  // is picked up by the next toast.
  const swipeDirection =
    typeof document !== "undefined" && document.documentElement.dir === "rtl" ? "right" : "left";

  return (
    <ToastContext.Provider value={ctxValue}>
      <RadixToast.Provider
        duration={defaultDuration}
        swipeDirection={swipeDirection}
        {...providerProps}
      >
        {children}
        {toasts.map((t) => (
          <ToastItem
            key={t.id}
            toast={t}
            defaultDuration={defaultDuration}
            paused={paused}
            onPointerOver={setPointerOver}
            onFocusInside={setFocusInside}
            onOpenChange={(open) => {
              if (!open) dismiss(t.id);
            }}
          />
        ))}
        <ToastViewport />
      </RadixToast.Provider>
    </ToastContext.Provider>
  );
}

/** Per-variant progress-bar tint — matches the toast's semantic accent. */
const PROGRESS_TINT: Record<ToastVariant, string> = {
  neutral: "bg-primary",
  info: "bg-info",
  success: "bg-success",
  warning: "bg-warning",
  danger: "bg-danger",
};

function ToastItem({
  toast,
  onOpenChange,
  defaultDuration,
  paused,
  onPointerOver,
  onFocusInside,
}: {
  toast: ToastEntry;
  onOpenChange: (open: boolean) => void;
  defaultDuration: number;
  paused: boolean;
  onPointerOver: (over: boolean) => void;
  onFocusInside: (inside: boolean) => void;
}) {
  const { t } = useTranslation("common");
  const variant = toast.variant ?? "neutral";
  const IconComp = VARIANT_ICON[variant];
  const iconName = VARIANT_ICON_NAME[variant];
  // Effective auto-dismiss window: per-toast override → provider default.
  // Only finite, positive durations are timed (Infinity = persistent, and the
  // toast then carries no progress bar because there is nothing to count).
  const effectiveDuration = toast.duration ?? defaultDuration;
  const timed = Number.isFinite(effectiveDuration) && effectiveDuration > 0;

  // The countdown, kept here rather than in Radix — see the file header. What
  // is left of it survives a pause, so a toast read halfway through does not
  // start over when the pointer leaves.
  const remainingRef = React.useRef(effectiveDuration);
  // The parent hands a fresh closure every render; the timer reads the latest
  // through a ref so a re-render never restarts the countdown.
  const closeRef = React.useRef(onOpenChange);
  React.useEffect(() => {
    closeRef.current = onOpenChange;
  });
  React.useEffect(() => {
    if (!timed || paused || remainingRef.current <= 0) return;
    const startedAt = Date.now();
    const timer = window.setTimeout(() => closeRef.current(false), remainingRef.current);
    return () => {
      window.clearTimeout(timer);
      remainingRef.current = Math.max(0, remainingRef.current - (Date.now() - startedAt));
    };
  }, [timed, paused]);

  return (
    <RadixToast.Root
      className={cn(toastVariants({ variant }))}
      onOpenChange={onOpenChange}
      duration={Infinity}
      // The pointer and the keyboard land on the toast itself, not on the
      // viewport around it, so the reading signals are read here.
      onPointerEnter={() => onPointerOver(true)}
      onPointerLeave={() => onPointerOver(false)}
      onFocusCapture={() => onFocusInside(true)}
      onBlurCapture={(e) => {
        const next = e.relatedTarget;
        if (!(next instanceof Node) || !e.currentTarget.contains(next)) onFocusInside(false);
      }}
    >
      <span className="mt-[2px] inline-flex">
        <Icon as={IconComp} name={iconName} size={18} />
      </span>
      <div className="flex-1">
        {toast.title !== undefined && (
          <RadixToast.Title className="text-heading text-body">{toast.title}</RadixToast.Title>
        )}
        <RadixToast.Description className="text-body">{toast.description}</RadixToast.Description>
      </div>
      {toast.action !== undefined && (
        <RadixToast.Action
          altText={toast.action.altText}
          onClick={toast.action.onClick}
          className={cn(
            "shrink-0 rounded-sm px-xs py-[2px] font-medium text-primary underline-offset-2",
            "hover:underline focus-visible:focus-ring",
          )}
        >
          {toast.action.label}
        </RadixToast.Action>
      )}
      <RadixToast.Close
        aria-label={toast.closeLabel ?? nonEmpty(t("actions.close"))}
        className="shrink-0 text-muted hover:text-default"
      >
        <Icon as={X} name="x" size={16} />
      </RadixToast.Close>
      {/* Timed-toast progress bar — drains inline-size 100%→0 over the toast's
          auto-dismiss window so the countdown is visible. Pinned to the block-end
          edge, aria-hidden (decorative; the toast body is the announced content).
          `inline-size` + `start-0` auto-mirror in RTL. */}
      {timed && (
        <span
          aria-hidden="true"
          className={cn(
            "pointer-events-none absolute bottom-0 start-0 block h-[2px] rounded-full",
            "animate-toast-progress",
            // The bar waits with the countdown it draws.
            paused && "[animation-play-state:paused]",
            PROGRESS_TINT[variant],
          )}
          style={{ ["--toast-duration" as string]: `${String(effectiveDuration)}ms` }}
        />
      )}
    </RadixToast.Root>
  );
}

// ---------------------------------------------------------------------------
// Dotted composition
// ---------------------------------------------------------------------------

export const Toast = {
  Provider: ToastProvider,
  Root: ToastRoot,
  Title: ToastTitle,
  Description: ToastDescription,
  Action: ToastAction,
  Close: ToastClose,
  Viewport: ToastViewport,
};
